import { createTheme, type Theme } from '@mui/material/styles';

// ICT brand tokens — sampled from www.ict.co's global.css.
// Keep in sync if marketing rebrands; this is the only source of brand
// truth in the status SPA.
const ICT = {
  red: '#C41444',
  dark: '#323E48',
  blue: '#003366',
  teal: '#6DCBD1',
  light: '#F6F6F6',
  border: '#A2A9AD',
  success: '#00B050',
};

/**
 * Build the MUI theme. Mode is derived from the OS / browser
 * `prefers-color-scheme` media query — there is no in-app theme toggle.
 *
 * Brand alignment:
 * - primary uses ICT teal (the friendlier accent on the marketing site).
 * - error uses ICT red — the brand's signature colour, so red surfaces on
 *   the status page read as "important" rather than "scary alarm".
 * - success uses the green from the marketing site's checkmark icons.
 * - typography uses Roboto Condensed for headings and Roboto for body
 *   (loaded via Google Fonts in index.html).
 */
export function buildTheme(mode: 'light' | 'dark'): Theme {
  const isDark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary: { main: ICT.teal, contrastText: ICT.dark },
      error: { main: ICT.red },
      success: { main: ICT.success },
      ...(isDark
        ? {
            background: { default: '#1A2128', paper: ICT.dark },
            text: { primary: '#F6F6F6', secondary: '#A2A9AD' },
            divider: 'rgba(162, 169, 173, 0.2)',
          }
        : {
            background: { default: ICT.light, paper: '#FFFFFF' },
            text: { primary: ICT.dark, secondary: '#5A6670' },
            divider: 'rgba(50, 62, 72, 0.12)',
          }),
    },
    typography: {
      fontFamily: ['Roboto', '-apple-system', 'BlinkMacSystemFont', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
      fontWeightLight: 300,
      fontWeightRegular: 300,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
        fontWeight: 400,
        fontSize: '2.25rem',
        lineHeight: 1.2,
      },
      h2: {
        fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
        fontWeight: 400,
        fontSize: '1.5rem',
        lineHeight: 1.2,
      },
      h3: {
        fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
        fontWeight: 400,
        fontSize: '1.125rem',
        lineHeight: 1.3,
      },
      body1: { fontWeight: 300 },
      body2: { fontWeight: 300 },
      button: {
        fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
        fontWeight: 400,
        textTransform: 'none',
      },
    },
    shape: { borderRadius: 4 },
  });
}
